// src/pages/Appli/InfoCN.jsx
import React from "react";
import useScrollToTop from "./../../../hooks/useScollToTop";
import Button from "../../../components/Button/Button";
import {
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaCalendarAlt,
  FaInfoCircle,
} from "react-icons/fa";

const HERO_URL = "/img/CentreNational/centre_national_02.jpg"; // ↔️ remplace par ton fichier (public/img/...)
const INFO_BANNER_URL = "/img/CentreNational/CN_info.png";
const INFO_BANNER2_URL = "/img/photo_PAAM_01.webp";
const PROGRAM_IMG_URL = "/img/CentreNational/cours_CN_new.jpg";
const ADDRESS_TXT = "Dojo PAMM - 153 rue Izzet Koç, 33310 Lormont";
const MAP_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(
  ADDRESS_TXT
)}&z=15&output=embed`;

// 🔗 Partenaires (logo et URL à adapter)
const PARTNERS = [
  {
    name: "Bordeaux Métropole",
    url: "https://www.bordeaux-metropole.fr/",
    logo: "/img/logos/logo_BM.webp",
  },
  {
    name: "Ligue Nouvelle-Aquitaine Judo",
    url: "https://nouvelle-aquitaine-judo.ffjudo.com/",
    logo: "/img/logos/logo_NA_fondblanc.jpg",
  },
  {
    name: "Ville de Lormont",
    url: "https://www.lormont.fr/",
    logo: "/img/logos/logo_lormont.jpg",
  },
  {
    name: "Conseil Départemental de la Gironde",
    url: "https://www.gironde.fr/",
    logo: "/img/logos/logo_departement.png",
  },
  {
    name: "Comité de Judo Gironde",
    url: "https://www.judogironde.fr/",
    logo: "/img/logos/logo_cd33.png",
  },
  {
    name: "Ministère des Sports",
    url: "https://www.sports.gouv.fr/",
    logo: "/img/logos/logo_ministere.png",
  },
  {
    name: "Agence Nationale du Sport",
    url: "https://www.agencedusport.fr/",
    logo: "/img/logos/logo_ans.png",
  },
  {
    name: "Région Nouvelle-Aquitaine",
    url: "https://www.nouvelle-aquitaine.fr/",
    logo: "/img/logos/logo_region_na.png",
  },
];

export default function InfoCN() {
  useScrollToTop();

  return (
    <div className="w-full pt-[70px] font-satoshi">
      <HeroImage />

      <HeaderBreadcrumb />

      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <QuickNav />

        <IntroSection />

        {/* === Bandeau d'infos (entre Ouverture et Piliers) === */}
        <section aria-label="Informations Centre National" className="mb-6">
          <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80">
            <img
              src={INFO_BANNER2_URL}
              alt="Informations essentielles du Centre National"
              className="h-auto object-contain max-x-[800px] mx-auto"
            />
          </div>
        </section>

        {/* === Bandeau d'infos (entre Ouverture et Piliers) === */}
        <section aria-label="Informations Centre National" className="mb-6">
          <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80">
            <img
              src={INFO_BANNER_URL}
              alt="Informations essentielles du Centre National"
              className="h-auto object-contain max-x-[800px] mx-auto"
            />
          </div>
        </section>

        <KeyPillars />

        <VeteransPrep />

        <PartnersSection />

        <MapSection />

        <PlanningPlaceholder />
      </div>
    </div>
  );
}

/* =========================
   Sous-composants
   ========================= */

function HeroImage() {
  return (
    <div className="relative h-[280px] md:h-[380px] w-full overflow-hidden">
      <img
        src={HERO_URL}
        alt="Centre National Nouvelle-Aquitaine — Judo Jujitsu"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
      <div className="relative z-10 max-w-[1200px] mx-auto h-full flex items-end px-4 pb-6">
        <div className="text-white drop-shadow">
          <h1 className="text-2xl md:text-4xl font-bold">
            Centre National Nouvelle-Aquitaine
          </h1>
          <p className="text-sm md:text-base opacity-90 mt-1">
            Entraînement • Formation • Performance • Transmission
          </p>
        </div>
      </div>
    </div>
  );
}

function HeaderBreadcrumb() {
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" />
      <div className="relative text-white px-4 py-4 md:py-5 max-w-[1200px] mx-auto">
        <p className="text-xs md:text-sm opacity-90">
          <a href="/" className="hover:underline">
            Accueil
          </a>{" "}
          &gt; Centre National
        </p>
      </div>
    </div>
  );
}

function QuickNav() {
  return (
    <nav
      aria-label="Sommaire"
      className="mb-6 rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-800/70 backdrop-blur p-3"
    >
      <ul className="flex flex-wrap gap-2">
        <li>
          <AnchorChip href="#presentation" label="Présentation" />
        </li>
        <li>
          <AnchorChip href="#piliers" label="Nos piliers" />
        </li>
        <li>
          <AnchorChip href="#prepa-veterans" label="Prépa Vétérans" />
        </li>
        <li>
          <AnchorChip href="#partenaires" label="Partenaires" />
        </li>
        <li>
          <AnchorChip href="#acces" label="Accès / Carte" />
        </li>
        <li>
          <AnchorChip href="#planning" label="Planning (à venir)" />
        </li>
      </ul>
    </nav>
  );
}

function AnchorChip({ href, label }) {
  return (
    <a
      href={href}
      className="px-3 py-1.5 rounded-full text-sm bg-white dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 ring-1 ring-slate-200/70 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
    >
      {label}
    </a>
  );
}

function IntroSection() {
  return (
    <section
      id="presentation"
      className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 shadow-sm p-6 mb-6"
    >
      <h2 className="text-xl md:text-2xl font-semibold mb-2">
        Ouverture du Centre National Nouvelle-Aquitaine Judo Jujitsu
      </h2>
      <p className="text-slate-700 dark:text-slate-200 leading-relaxed">
        La Ligue Nouvelle-Aquitaine et le Comité de Gironde sont heureux
        d’annoncer l’ouverture du{" "}
        <strong>Centre National Nouvelle-Aquitaine Judo Jujitsu</strong>, un
        lieu entièrement dédié à l’entraînement, à la formation et au
        développement de notre communauté. Situé au{" "}
        <em>Dojo PAMM, 153 rue Izzet Koç, 33310 Lormont</em>, ce centre
        s’adresse aux judokas, enseignants, dirigeants et à tous les passionnés
        de notre discipline.
      </p>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <InfoCard
          title="Entraînements dédiés"
          items={[
            "Judo",
            "Jujitsu",
            "Ne-waza",
            "Fighting",
            "Séances spécifiques Vétérans",
          ]}
        />
        <InfoCard
          title="Formations"
          items={[
            "Formation professionnelle (CQP)",
            "Formation fédérale (CAF & CAP)",
            "Formation des dirigeants",
            "Prochainement : formation continue",
          ]}
        />
      </div>
    </section>
  );
}

function InfoCard({ title, items = [] }) {
  return (
    <div className="rounded-xl p-4 ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50/70 dark:bg-slate-900/40">
      <div className="flex items-center gap-2 mb-2">
        <FaInfoCircle className="text-slate-500 dark:text-slate-300" />
        <h3 className="font-medium">{title}</h3>
      </div>
      <ul className="text-sm text-slate-700 dark:text-slate-300 list-disc pl-5">
        {items.map((it) => (
          <li key={it} className="leading-6">
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function KeyPillars() {
  return (
    <section
      id="piliers"
      className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 shadow-sm p-6 mb-6"
    >
      <h2 className="text-xl md:text-2xl font-semibold mb-2">Nos piliers</h2>
      <div className="grid gap-4 md:grid-cols-3">
        <Pillar
          title="Performance"
          desc="Un cadre d’entraînement exigeant, porté par une équipe expérimentée, pour progresser et performer toute l’année."
        />
        <Pillar
          title="Transmission"
          desc="Des parcours de formation reconnus (CQP, CAF, CAP) et bientôt de la formation continue pour structurer le réseau d’enseignants."
        />
        <Pillar
          title="Ouverture"
          desc="Un lieu de convivialité et d’échanges, au service des clubs, enseignants et dirigeants de toute la région."
        />
      </div>
    </section>
  );
}

function Pillar({ title, desc }) {
  return (
    <div className="rounded-xl p-4 ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50/70 dark:bg-slate-900/40">
      <h3 className="font-medium mb-1">{title}</h3>
      <p className="text-sm text-slate-700 dark:text-slate-300">{desc}</p>
    </div>
  );
}

function VeteransPrep() {
  return (
    <section
      id="prepa-veterans"
      className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 shadow-sm p-6 mb-6"
    >
      <h2 className="text-xl md:text-2xl font-semibold">
        Préparation Vétérans – Olympiade 2025-2026
      </h2>
      <p className="text-slate-700 dark:text-slate-200 mt-2 leading-relaxed">
        Une préparation spécifique destinée aux judokas vétérans est mise en
        place au sein du Centre pour la nouvelle olympiade. Encadré par une
        équipe expérimentée, ce programme vise à accompagner la progression et
        la préparation aux compétitions, avec une attention particulière portée
        aux athlètes visant les{" "}
        <strong>Championnats d’Europe Vétérans – juin 2026</strong>.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 ring-1 ring-slate-200/70 dark:ring-slate-600">
          <FaCalendarAlt />
          Début des entraînements : <strong>10 novembre 2025</strong>
        </span>
        {/* <Button
          text="Contacter la coordination"
          variant="outline"
          size="sm"
          onClick={() => (window.location.href = "mailto:contact@exemple.fr")}
        /> */}
      </div>
    </section>
  );
}

function PartnersSection() {
  return (
    <section
      id="partenaires"
      className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 shadow-sm p-6 mb-6"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl md:text-2xl font-semibold">Partenaires</h2>
        <span className="text-sm px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
          {PARTNERS.length} partenaires
        </span>
      </div>

      <div className="mt-4 grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
        {PARTNERS.map((p) => (
          <a
            key={p.name}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group rounded-xl p-3 ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50/70 dark:bg-slate-900/40 hover:shadow-sm transition flex flex-col items-center text-center"
            title={`Visiter le site de ${p.name}`}
          >
            <div className="w-24 h-24 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-white flex items-center justify-center">
              <img
                src={p.logo || "/img/no-image.jpg"}
                alt={p.name}
                className="w-full h-full object-contain group-hover:scale-105 transition"
              />
            </div>
            <div className="mt-2 text-sm text-slate-800 dark:text-slate-100 line-clamp-2">
              {p.name}
            </div>
            <div className="mt-1 text-xs text-indigo-600 dark:text-indigo-400 inline-flex items-center gap-1">
              Voir le site
              <FaExternalLinkAlt className="text-[10px]" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function MapSection() {
  return (
    <section
      id="acces"
      className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 shadow-sm p-6 mb-6"
    >
      <h2 className="text-xl md:text-2xl font-semibold">Accès au Centre</h2>

      <div className="mt-2 text-slate-700 dark:text-slate-200">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-700">
          <FaMapMarkerAlt />
          <span className="font-medium">{ADDRESS_TXT}</span>
        </div>
      </div>

      <div className="mt-4 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-slate-50 dark:bg-slate-900">
        <iframe
          title="Localisation du Centre National"
          src={MAP_EMBED}
          className="w-full h-[340px] md:h-[420px] border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>

      <div className="mt-3">
        <Button
          text="Ouvrir dans Google Maps"
          variant="primary"
          onClick={() =>
            window.open(
              `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                ADDRESS_TXT
              )}`,
              "_blank",
              "noopener,noreferrer"
            )
          }
          icon={FaExternalLinkAlt}
          size="sm"
        />
      </div>
    </section>
  );
}

function PlanningPlaceholder() {
  return (
    <section
      id="planning"
      className="rounded-2xl ring-1 ring-dashed ring-slate-300 dark:ring-slate-700 bg-white/70 dark:bg-slate-800/60 p-6 mb-6"
    >
      <h2 className="text-lg md:text-xl font-semibold">Planning des cours</h2>
      <div className="mt-4 rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white">
        <img
          src={PROGRAM_IMG_URL}
          alt="Programme des cours et événements du Centre National"
          className="h-auto object-contain max-x-[600px] mx-auto"
        />
      </div>
    </section>
  );
}
